import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, ArrowLeft, X, Check, Award } from "lucide-react";

const NAVY = "#0b192c", GOLD = "#c4a454", MUTED = "#94a3b8", BORDER = "#e2e1dd";

// Steps deliberately show the *money features* only — every step lands on a
// real, populated page in the demo sandbox.
const STEPS = [
  {
    key: "dashboard",
    to: "/dashboard",
    title: "Your fund at a glance",
    body: "The dashboard rolls up AUM, capital called, LPs onboarded, compliance and open tasks — every KPI you'd fight to see in a PPT, live for your fund.",
  },
  {
    key: "investors",
    to: "/investors",
    title: "Investor CRM without spreadsheets",
    body: "Every LP with their KYC, commitment, called-and-paid, unit balance and onboarding stage. Bulk BENPOS auto-imports and one-click Sandbox KYC included.",
  },
  {
    key: "capital-calls",
    to: "/capital-calls",
    title: "Capital calls in 60 seconds",
    body: "Issue a drawdown, auto-generate LP notices, watch payment reconciliation in real-time — no more Excel formulas or manual notice PDFs.",
  },
  {
    key: "nav",
    to: "/nav-history",
    title: "NAV & valuations, publish-ready",
    body: "Quarterly NAV, per-scheme unit price, historical AUM chart — everything the trustee and auditor need, one click away.",
  },
];

const STORAGE_KEY = (demoId) => `aif.demo.tour.dismissed.${demoId || "anon"}`;

export default function DemoTour() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user?.is_demo || !user?.demo_id) return;
    const dismissed = localStorage.getItem(STORAGE_KEY(user.demo_id));
    if (dismissed) return;
    // Wait a beat so the dashboard shell mounts first.
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, [user?.is_demo, user?.demo_id]);

  if (!open) return null;

  const s = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const finish = () => {
    if (user?.demo_id) localStorage.setItem(STORAGE_KEY(user.demo_id), "1");
    setOpen(false);
  };

  const downloadCertificate = async () => {
    try {
      const resp = await api.get("/demos/certificate.pdf", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AIFLedger_Certificate_${(user?.name || "prospect").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };
  const next = () => {
    if (isLast) return finish();
    const nextStep = step + 1;
    setStep(nextStep);
    if (STEPS[nextStep].to) navigate(STEPS[nextStep].to);
  };
  const prev = () => {
    if (step === 0) return;
    const prevStep = step - 1;
    setStep(prevStep);
    if (STEPS[prevStep].to) navigate(STEPS[prevStep].to);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 sm:p-8 pointer-events-none">
      {/* Dim only slightly so users can still see the page behind the card. */}
      <div className="absolute inset-0 bg-black/25 pointer-events-auto" onClick={finish} />
      <div
        className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full pointer-events-auto"
        style={{ border: `1px solid ${BORDER}` }}
        data-testid="demo-tour"
      >
        <button
          onClick={finish}
          className="absolute top-3 right-3 rounded-full p-1 hover:bg-[#faf9f5] transition"
          aria-label="Close tour"
          data-testid="demo-tour-close"
        >
          <X size={16} style={{ color: MUTED }} />
        </button>
        <div className="px-7 pt-7 pb-3">
          <div className="text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1.5"
               style={{ color: GOLD }}>
            <Sparkles size={12} /> Guided demo · step {step + 1} of {STEPS.length}
          </div>
          <div className="font-serif text-2xl mt-2 leading-tight" style={{ color: NAVY }}>
            {s.title}
          </div>
          <p className="text-sm mt-3 leading-relaxed" style={{ color: "#475569" }}>
            {s.body}
          </p>
        </div>
        {/* Progress dots */}
        <div className="px-7 pb-2 flex items-center gap-1.5">
          {STEPS.map((_, i) => (
            <div key={i}
                 className="h-1 rounded-full transition-all flex-1"
                 style={{
                   background: i <= step ? GOLD : "#e2e1dd",
                   opacity: i <= step ? 1 : 0.6,
                 }} />
          ))}
        </div>
        <div className="px-7 pt-3 pb-6 flex items-center justify-between flex-wrap gap-2">
          <button onClick={finish} className="text-xs" style={{ color: MUTED }}
                  data-testid="demo-tour-skip">
            Skip tour
          </button>
          <div className="flex gap-2">
            {isLast && (
              <Button size="sm" variant="outline" onClick={downloadCertificate}
                      className="border-[#c4a454] text-[#8b6f2a]"
                      data-testid="demo-tour-certificate">
                <Award size={12} className="mr-1" /> Certificate
              </Button>
            )}
            {step > 0 && (
              <Button size="sm" variant="outline" onClick={prev} className="border-[#e2e1dd]"
                      data-testid="demo-tour-prev">
                <ArrowLeft size={12} className="mr-1" /> Back
              </Button>
            )}
            <Button size="sm" onClick={next}
                    className="bg-[#0b192c] hover:bg-[#0b192c]/90"
                    data-testid="demo-tour-next">
              {isLast ? (<><Check size={12} className="mr-1" /> Start exploring</>)
                     : (<>Next <ArrowRight size={12} className="ml-1" /></>)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
